/**
 * The data layer every screen talks to. Reads come from an in-memory mirror of the local
 * store; writes land locally first (so they appear immediately, connection or not) and are
 * queued for the server. Components never decide "am I online" — this file does.
 */

import { Aborted, ApiError } from '~/api/client'
import type { Api, MediaSlot, PutSetBody } from '~/api/endpoints'
import type { LocalStore } from '~/data/local-store'
import {
  numbersOf,
  recordsOf,
  type Exercise,
  type LoggedSet,
  type DistanceUnits,
  type Mood,
  type PendingOp,
  type RecentExercise,
  type SetInput,
  type TimelineDay,
  type Units,
  type Workout,
} from '~/data/types'
import { nextWorkout, normalizeExerciseName } from '@gymcloud/mixedscript'
import { findsSet, readSearch } from '@gymcloud/set-search'
import {
  parseTemplate,
  scriptName,
  toHistory,
  type ActiveTemplate,
  type Enrollment,
  type ExploreQuery,
  type LibraryListing,
  type LibraryTemplate,
  type PlanEntry,
  type ContextSession,
  type ContextSet,
  type TemplateContext,
  type TemplateVersion,
  type WorkoutsDone,
  type WorkoutTemplate,
} from '~/data/templates'
import {
  SETTINGS_ID,
  type Change,
  type SyncKind,
  type SyncRows,
  type FavouriteRow,
  type MealFavouriteRow,
  type NutritionDay,
  type SettingsRow,
  type WorkoutRow,
  byNewestUpdate,
  updatedAtOf,
  workoutFromWire,
} from '~/data/sync'
import type { DailyMetric } from '~/data/metrics'
import { entryBody, mealAsKept } from '~/data/nutrition'
import type { Entry, Food, Goal, Meal, MealLog } from '~/data/nutrition'
import type { WeighIn } from '~/data/weighins'
import { groupsOf } from '~/lib/set-runs'
import { contextHere, readingHere, searchableOf } from '~/lib/set-search-here'
import { dayKey } from '~/lib/dates'
import { daysIntoCycle } from '~/lib/weekdays'

export interface DataState {
  /** True once the first server pass has either landed or yielded to the device cache. */
  readonly timelineSettled: boolean
  readonly exercises: Exercise[]
  /** Newest first. */
  readonly sets: LoggedSet[]
  /** Finished workouts, newest first. */
  readonly workouts: Workout[]
  readonly currentWorkout: Workout | null
  /**
   * The server's exercises row: every exercise with sets, most recently done first. Sets
   * this device holds are laid over it at render time, so it can start empty offline.
   */
  readonly recentExercises: RecentExercise[]
  /** True once the timeline's oldest server page has been reached. */
  readonly timelineComplete: boolean
  /**
   * How many sets fall on each day of the whole history, newest day first — the timeline's
   * shape, which is cheap to know in full even though the sets themselves arrive a window at
   * a time.
   */
  readonly timelineIndex: TimelineDay[]
  /** Your library of templates, most recently touched first. */
  readonly templates: WorkoutTemplate[]
  /** Every run, newest first. The one still going is the active template. */
  readonly enrollments: Enrollment[]
  /** The scripts runs follow, for the runs of templates that are not yours to hold. */
  readonly versions: TemplateVersion[]
  /** Whether there is a connection. Waiting for one is not the same as waiting a turn. */
  readonly online: boolean
  /**
   * True once the feed has been pulled at least once, however that went. Everything below is
   * null until then: a screen must not claim an empty diary it has not read.
   */
  readonly synced: boolean
  /**
   * Signed in, and this browser has never read the account through. What it holds is not the
   * account but nothing yet, so a screen with nothing to show says that rather than spinning.
   */
  readonly couldNotBeRead: boolean
  readonly weighins: WeighIn[] | null
  readonly metrics: DailyMetric[] | null
  readonly entries: Entry[] | null
  /** Only the days said to be a partial record; a day absent was never marked. */
  readonly days: NutritionDay[] | null
  readonly goals: Goal[] | null
  /** The account's own foods and recipes. */
  readonly foods: Food[] | null
  readonly favourites: FavouriteRow[] | null
  readonly meals: Meal[] | null
  readonly mealFavourites: MealFavouriteRow[] | null
  readonly mealLogs: MealLog[] | null
  /** The account's own settings as the feed carries them. */
  readonly settings: SettingsRow | null
}

/**
 * What asking the server for one thing came to. `unreachable` is not `gone`: a page that could
 * not get an answer has been told nothing, and only the server saying there is no such thing
 * makes it gone.
 */
export type Lookup = 'found' | 'gone' | 'unreachable'

export interface SearchParams {
  /** What was typed — read as words and numbers, see `/specs/features/search.spec.md`. */
  readonly exercise: string
  /** The account's weight units, which a bare number is read in. Kilograms where unsaid. */
  readonly units?: Units
  /** And the distance units, which a bare distance on `distance:` is read in. */
  readonly distanceUnits?: DistanceUnits
  /** The workouts and the runs the device holds, which is what a set was part of. */
  readonly workouts?: readonly Workout[]
  readonly enrollments?: readonly Enrollment[]
  readonly filmed?: boolean
  /** `false` leaves warm-up sets out; anything else leaves them in. */
  readonly warmups?: boolean
}

export interface DataServiceDependencies {
  readonly api: Api
  readonly store: LocalStore
  readonly uuid?: () => string
  readonly now?: () => number
}

/** What a workout carries beyond its sets: what it was called, how it felt, when it ended. */
export interface WorkoutDetails {
  readonly name?: string | null
  readonly mood?: Mood | null
  readonly openingNotes?: string | null
  readonly closingNotes?: string | null
  readonly finishedAt?: number | null
  /** The run it was played under, and which of the template's workouts it was. */
  readonly enrollmentId?: string | null
  readonly templateWorkout?: string | null
  /** Every set the template planned, with `loggedIndex` pointing into the sets given. */
  readonly plan?: PlanEntry[]
}

export interface DataService {
  getState: () => DataState
  subscribe: (listener: () => void) => () => void

  /** Loads the on-device cache into memory. Await before first render. */
  init: () => Promise<void>
  /** Pulls fresh data from the server; quietly a no-op while offline. */
  refresh: () => Promise<void>
  loadOlderSets: () => Promise<void>
  /** Fetch the sets for a contiguous run of days from the timeline index. */
  loadDays: (days: readonly TimelineDay[]) => Promise<void>
  /**
   * Fetch every set of one exercise, however far back it goes, into the device's pile.
   * The exercise page's PRs and graph are drawn from the whole history, not a window.
   */
  loadExerciseHistory: (exerciseName: string) => Promise<boolean>
  /** The workouts an exercise was done in, newest first; what the device holds while offline. */
  /** Sends queued writes to the server, stopping at the first sign of no connection. */
  flush: () => Promise<void>
  /**
   * Waits for the queue to be sent, not merely for whatever pass is running — a pass already
   * under way may have read the queue before the op being waited on joined it.
   */
  drain: () => Promise<void>

  /**
   * Asks the server for one set the device does not hold, and keeps it. What the timeline has
   * pulled is a window, so a set older than it — or one reached from a search, an address, a
   * link — is missing from the pile without being missing from the account.
   */
  loadSet: (id: string) => Promise<Lookup>
  /** The same for one workout, with the sets in it. */
  loadWorkout: (id: string) => Promise<Lookup>

  editSet: (id: string, input: SetInput) => Promise<void>
  deleteSet: (id: string) => Promise<void>

  /** What a template needs before it can be evaluated: its run's sessions and the history. */
  templateContext: (names: string[], enrollmentId: string | null) => Promise<TemplateContext>

  /** Keeps a template. The name is read out of the script, so a list costs no parsing. */
  saveTemplate: (
    id: string | null,
    script: string,
    forkedFromVersionId?: string | null,
  ) => Promise<WorkoutTemplate>
  deleteTemplate: (id: string) => Promise<void>
  /** Puts a template into Explore, or takes it out. Both need a connection. */
  publishTemplate: (id: string) => Promise<LibraryListing>
  unpublishTemplate: (id: string) => Promise<void>
  /** The published templates, narrowed by the query. Needs a connection. */
  explore: (query: ExploreQuery) => Promise<LibraryListing[]>
  /** One published template with everything its page shows. Needs a connection. */
  exploreTemplate: (templateId: string) => Promise<LibraryTemplate>
  /** What you have done of a template, by workout, across every run of it. */
  templateWorkoutsDone: (templateId: string) => Promise<WorkoutsDone[]>
  /** Takes a copy of a published template into your library, ready to edit. */
  copyTemplate: (listing: LibraryTemplate) => Promise<WorkoutTemplate>
  /**
   * Starting a template. Only one runs at a time; starting again resets its progressions.
   * `anchorDay` is the weekday a strict cycle begins on, and nothing on any other schedule.
   */
  enroll: (templateId: string, anchorDay: number | null) => Promise<Enrollment>
  /** Starting a published template, at exactly the version Explore offers. */
  enrollInListing: (listing: LibraryTemplate, anchorDay: number | null) => Promise<Enrollment>
  /** Moving a run on to a newer version. The run stays, so its progressions carry on. */
  moveRunToVersion: (enrollmentId: string, versionId: string) => Promise<void>
  /**
   * Moving which weekday the run's cycle begins on. The cycle is laid out afresh from the day
   * the run began, so what it offers today can change; nothing logged under it moves.
   */
  setRunAnchor: (enrollmentId: string, anchorDay: number | null) => Promise<void>
  endEnrollment: (id: string) => Promise<void>
  /** Puts an ended run back as the active one — the undo of ending it. */
  resumeEnrollment: (id: string) => Promise<void>
  /** Restores a past run, throwing the current one away entirely. */
  restoreEnrollment: (id: string) => Promise<void>
  /**
   * The active template with everything a plan needs behind it. The answer is kept against
   * the run, its script and what has been logged under it, so asking for it again costs
   * nothing until one of those moves.
   */
  activeTemplate: () => Promise<ActiveTemplate | null>
  /**
   * The kept answer, without waiting — `null` when there is none to hand, and `{ value }`
   * when there is, since the answer itself can be null. A page opens with this and asks for
   * the real one behind it.
   */
  heldActiveTemplate: () => { value: ActiveTemplate | null } | null
  /** How long a screen may keep today's schedule before the local calendar turns over. */
  millisecondsUntilNextCalendarDay: () => number
  renameWorkout: (id: string, name: string) => Promise<void>
  deleteWorkout: (id: string) => Promise<void>
  workoutWithSets: (id: string) => { workout: Workout; sets: LoggedSet[] } | null

  mediaUrl: (setId: string, slot: MediaSlot) => string

  /**
   * A row this device has just written, laid into the mirror before the server hears of it —
   * so the screen shows the change in the same breath as the press that made it.
   */
  keep: <K extends SyncKind>(kind: K, id: string, row: SyncRows[K]) => Promise<void>
  /** The same for a row taken away. */
  drop: (kind: SyncKind, id: string) => Promise<void>
  /**
   * Entries logged, laid onto their days and queued to be sent — the one diary write that
   * never waits on the server, since the key that logs is pressed on a page that goes with it.
   */
  logEntries: (entries: readonly Entry[]) => Promise<void>
  /**
   * Several rows at once, laid in together and read back once. One press that changed five
   * entries is one change to the screen: laid in one at a time they arrive one at a time, and
   * undoing the removal of five things looked like five things being put back in turn rather
   * than like the one press being undone.
   */
  keepAll: <K extends SyncKind>(
    kind: K,
    rows: readonly { id: string; row: SyncRows[K] }[],
    gone?: readonly string[],
  ) => Promise<void>

  /** Drops everything on the device — sign-out and account deletion end here. */
  reset: () => Promise<void>
}

const byNewestFirst = (a: LoggedSet, b: LoggedSet) =>
  b.performedAt - a.performedAt || (a.id < b.id ? 1 : -1)

const byRecentlyFinished = (a: Workout, b: Workout) =>
  (b.finishedAt ?? 0) - (a.finishedAt ?? 0) || (a.id < b.id ? 1 : -1)

function numbersFor(
  set: LoggedSet | SetInput,
): Pick<PutSetBody, 'weightKg' | 'reps' | 'durationSeconds' | 'distanceMeters'> {
  return {
    ...(set.weightKg != null ? { weightKg: set.weightKg } : {}),
    ...(set.reps != null ? { reps: set.reps } : {}),
    ...(set.durationSeconds != null ? { durationSeconds: set.durationSeconds } : {}),
    ...(set.distanceMeters != null ? { distanceMeters: set.distanceMeters } : {}),
  }
}

/**
 * The sets a search finds, off the whole of what the device holds. A pure reading rather than
 * a request: every set the account has is in the mirror, so what a search answers depends only
 * on what is held at the moment it is asked — which is what lets a page re-derive it as the
 * feed lands instead of asking once and keeping a stale answer.
 */
export function searchSets(sets: LoggedSet[], params: SearchParams): LoggedSet[] {
  // Read once, and each set asked of what was read.
  const terms = readSearch(params.exercise, readingHere())
  const context = contextHere(params.units ?? 'kg', params.distanceUnits ?? 'km')
  const workouts = new Map((params.workouts ?? []).map((workout) => [workout.id, workout]))
  const runs = new Map((params.enrollments ?? []).map((run) => [run.id, run]))
  return sets.filter((set) => {
    if (!findsSet(terms, searchableOf(set, workouts, runs), context)) {
      return false
    }
    if (params.filmed === true && !set.hasVideo) {
      return false
    }
    if (params.warmups === false && set.isWarmup) {
      return false
    }
    return true
  })
}

/** The workouts an exercise was done in, newest first — the same, and for the same reason. */
export function workoutsOfExercise(
  exerciseName: string,
  workouts: Workout[],
  sets: LoggedSet[],
): Workout[] {
  const key = normalizeExerciseName(exerciseName)
  return workouts
    .filter((workout) =>
      sets.some(
        (set) => set.workoutId === workout.id && normalizeExerciseName(set.exerciseName) === key,
      ),
    )
    .sort((a, b) => (b.finishedAt ?? 0) - (a.finishedAt ?? 0))
}

export function createDataService(deps: DataServiceDependencies): DataService {
  const { api, store } = deps
  const uuid = deps.uuid ?? (() => crypto.randomUUID())
  const now = deps.now ?? (() => Date.now())

  let state: DataState = {
    timelineSettled: false,
    exercises: [],
    sets: [],
    workouts: [],
    currentWorkout: null,
    recentExercises: [],
    timelineComplete: false,
    timelineIndex: [],
    templates: [],
    enrollments: [],
    versions: [],
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
    synced: false,
    couldNotBeRead: false,
    weighins: null,
    metrics: null,
    entries: null,
    days: null,
    goals: null,
    foods: null,
    favourites: null,
    meals: null,
    mealFavourites: null,
    mealLogs: null,
    settings: null,
  }
  /** The pass under way, so a caller that waits gets a promise that actually settles. */
  let running: Promise<void> | null = null
  /**
   * Ops taken out of the queue while a pass was part way through it.
   *
   * A pass works from a snapshot, so an op a delete removed mid-pass would still be sent —
   * putting the row it belongs to straight back on the server after the delete threw it away.
   */
  const droppedMidPass = new Set<string>()
  /**
   * Sets deleted here whose delete has not reached the server yet. A page asking the server
   * for a day it is showing would otherwise be handed one of them back and put it on screen
   * again — a set deleted a moment ago reappearing under the finger that deleted it.
   */
  const deletedHere = new Set<string>()
  const listeners = new Set<() => void>()

  // They sync once a connection returns. Which of the two it is has to reach the screens as
  // well: a queue stalled for want of a connection is not a queue waiting its turn.
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      setState({ online: true })
      void flush()
    })
    window.addEventListener('offline', () => {
      setState({ online: false })
    })
    /*
     * A tab looked at again is a tab that has been away, and whatever else was changed
     * meanwhile is what the feed has been holding. Cheap: it asks for what has moved since
     * this device last read, which after a moment away is nothing at all.
     */
    window.addEventListener('focus', () => {
      // Whatever is queued goes before what has moved is read — see `syncNow`.
      void syncNow()
    })
  }

  function setState(patch: Partial<DataState>): void {
    state = { ...state, ...patch }
    for (const listener of listeners) {
      listener()
    }
  }

  /** The per-day shape of a pile of sets, newest day first, each day read in order. */
  function indexOfSets(sets: readonly LoggedSet[]): TimelineDay[] {
    const byDay = new Map<string, LoggedSet[]>()
    for (const set of [...sets].sort(byNewestFirst)) {
      const day = dayKey(set.performedAt)
      byDay.set(day, [...(byDay.get(day) ?? []), set])
    }
    return [...byDay.entries()]
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([day, daySets]) => ({
        day,
        count: daySets.length,
        groups: groupsOf(
          [...daySets].reverse(),
          (id) => state.workouts.find((workout) => workout.id === id)?.name ?? null,
        ),
      }))
  }

  /**
   * The index with the given days redescribed by what this device holds. Removing sets leaves
   * the index counting ones that are not there, and the timeline would hold room for them —
   * and keep naming the workout they were in — forever; a day emptied that way goes entirely.
   */
  function reindexed(
    index: readonly TimelineDay[],
    days: ReadonlySet<string>,
    sets: readonly LoggedSet[],
  ): TimelineDay[] {
    if (days.size === 0) {
      return [...index]
    }
    const fresh = indexOfSets(sets.filter((set) => days.has(dayKey(set.performedAt))))
    return [...index.filter((day) => !days.has(day.day)), ...fresh].sort((a, b) =>
      a.day < b.day ? 1 : -1,
    )
  }

  /**
   * Why a lookup came back with nothing. Only the server saying there is no such thing is
   * gone; a connection that failed and a server that broke are both "we could not ask".
   */
  function lookupFailure(error: unknown): Lookup {
    return error instanceof ApiError && error.status === 404 ? 'gone' : 'unreachable'
  }

  /** An error that is not the server answering means the connection is the problem. */
  function isOffline(error: unknown): boolean {
    return !(error instanceof ApiError)
  }

  // Omit does not distribute over a union by itself, hence the conditional helper type.
  type WithoutId<T> = T extends { id: string } ? Omit<T, 'id'> : never

  async function enqueue(op: WithoutId<PendingOp>): Promise<void> {
    await store.enqueueOp({ ...op, id: uuid() })
  }

  function findSet(id: string): LoggedSet | null {
    return state.sets.find((set) => set.id === id) ?? null
  }

  async function sendOp(op: PendingOp): Promise<void> {
    switch (op.kind) {
      case 'put-set': {
        const set = findSet(op.setId)
        if (!set) {
          return
        }
        /*
         * The whole set, not only its numbers. `PUT /sets/:id` writes the row it is given, so
         * anything left out of the body is not left alone — it is written away. A set logged
         * with a note, an effort, a warm-up mark, a superset or the times off its recording
         * would come back from a later edit with all of that gone.
         */
        await api.putSet(set.id, {
          exerciseName: set.exerciseName,
          performedAt: set.performedAt,
          workoutId: set.workoutId,
          isWarmup: set.isWarmup,
          notes: set.notes,
          rpe: set.rpe,
          supersetGroup: set.supersetGroup ?? null,
          supersetRound: set.supersetRound ?? null,
          startedAt: set.startedAt ?? null,
          endedAt: set.endedAt ?? null,
          ...numbersFor(set),
        })
        return
      }
      case 'delete-set':
        await api.deleteSet(op.setId)
        // The server is rid of it, so nothing can hand it back and the guard can go.
        deletedHere.delete(op.setId)
        return
      case 'put-workout': {
        const workout =
          state.currentWorkout?.id === op.workoutId
            ? state.currentWorkout
            : state.workouts.find((w) => w.id === op.workoutId)
        if (!workout) {
          return
        }
        await api.putWorkout(workout.id, {
          startedAt: workout.startedAt,
          finishedAt: workout.finishedAt,
          name: workout.name,
          mood: workout.mood ?? null,
          openingNotes: workout.openingNotes ?? null,
          closingNotes: workout.closingNotes ?? null,
          enrollmentId: workout.enrollmentId ?? null,
          templateWorkout: workout.templateWorkout ?? null,
          plan: (workout.plan ?? []).map((entry) => ({
            exerciseName: entry.exerciseName,
            isWarmup: entry.isWarmup,
            target: entry.target,
            setId: entry.setId ?? null,
            logged: (entry.setId ?? null) !== null,
            // Sent back as it came, or a workout saved here would make the assistant's sets planned.
            byAssistant: entry.byAssistant === true,
          })),
        })
        return
      }
      case 'delete-workout':
        await api.deleteWorkout(op.workoutId)
        return
      case 'put-template': {
        // Read from the store rather than memory: a flush that overtakes a state update must
        // not mistake "not there yet" for "done".
        const templates = await store.loadTemplates()
        const template =
          templates.find((held) => held.id === op.templateId) ??
          state.templates.find((held) => held.id === op.templateId)
        if (!template) {
          return
        }
        await api.putTemplate(template.id, {
          name: template.name,
          script: template.script,
          forkedFromVersionId: template.forkedFromVersionId ?? null,
        })
        return
      }
      case 'delete-template':
        await api.deleteTemplate(op.templateId)
        return
      case 'enroll': {
        const enrollments = await store.loadEnrollments()
        const enrollment =
          enrollments.find((held) => held.id === op.enrollmentId) ??
          state.enrollments.find((held) => held.id === op.enrollmentId)
        if (!enrollment) {
          return
        }
        /*
         * A run started from Explore names its version, which is the whole point of it: the
         * script somebody signed up for is the one they get. A run of your own template names
         * the template instead and takes whatever it says when this lands, since the version
         * it will follow is the server's to mint.
         */
        await api.enroll({
          id: enrollment.id,
          ...(enrollment.versionId.length > 0
            ? { versionId: enrollment.versionId }
            : { templateId: enrollment.templateId ?? '' }),
          startedAt: enrollment.startedAt,
          anchorDay: enrollment.anchorDay ?? null,
        })
        return
      }
      case 'set-anchor': {
        const enrollments = await store.loadEnrollments()
        const enrollment =
          enrollments.find((held) => held.id === op.enrollmentId) ??
          state.enrollments.find((held) => held.id === op.enrollmentId)
        if (!enrollment) {
          return
        }
        // The device's answer wins: whatever it holds now is what the server is told, so two
        // changes made offline land as the last one rather than as both in turn.
        await api.setEnrollmentAnchor(enrollment.id, enrollment.anchorDay ?? null)
        return
      }
      case 'end-enrollment':
        await api.endEnrollment(op.enrollmentId)
        return
      case 'delete-enrollment':
        await api.deleteEnrollment(op.enrollmentId)
        return
      case 'put-entry': {
        /*
         * Read from the store, as a template is, and sent as it stands when this lands: an
         * entry changed or taken off the day between the press and the send is sent as it is
         * now, or not at all.
         */
        const entry = (await store.loadRows('entry')).get(op.entryId)
        if (entry === undefined) {
          return
        }
        await api.putEntry(entry.id, entryBody(entry))
        return
      }
    }
  }

  /** Which kind of row a queued write is about. */
  function kindOf(op: PendingOp): SyncKind {
    switch (op.kind) {
      case 'put-set':
      case 'delete-set':
        return 'set'
      case 'put-workout':
      case 'delete-workout':
        return 'workout'
      case 'put-template':
      case 'delete-template':
        return 'template'
      case 'put-entry':
        return 'entry'
      default:
        return 'enrollment'
    }
  }

  /**
   * A write the server refused as older than the row it would have overwritten. The row it
   * hands back with the refusal takes the mirror's place and what this device was holding is
   * dropped — the write is not retried, and the row the account really has is what is shown.
   */
  async function yieldToServer(op: PendingOp, error: unknown): Promise<boolean> {
    if (!(error instanceof ApiError) || error.status !== 409) {
      return false
    }
    const said = error.body as { error?: string; row?: { id?: string } } | null
    if (said?.error !== 'stale') {
      return false
    }
    const row = said.row
    if (row?.id === undefined) {
      return true
    }
    await store.putRow(kindOf(op), row.id, row as never)
    await rebuild()
    return true
  }

  async function sendQueued(): Promise<boolean> {
    droppedMidPass.clear()
    let sent = false
    const ops = await store.loadOps()
    for (const op of ops) {
      // Nothing retries on its own: a refusal waits to be asked again, and asking is the
      // only thing that clears the mark. Without this the next flush would try it forever.
      if (op.failed === true || droppedMidPass.has(op.id)) {
        continue
      }
      try {
        await sendOp(op)
      } catch (error) {
        if (error instanceof Aborted) {
          // Somebody stopped this one on purpose, and its op went with the cancel. Nothing is
          // wrong with the connection, so the rest of the queue is still owed.
          continue
        }
        if (isOffline(error)) {
          return sent
        }
        // Refused as stale: the server's row wins, and this one is dropped rather than tried
        // again. Anything else refused is dropped too — nothing here retries on its own.
        await yieldToServer(op, error)
      }
      sent = true
      await store.removeOp(op.id)
    }
    return sent
  }

  /**
   * One pass at a time. Callers that do not wait get the pass already under way rather than a
   * second one beside it; callers that do wait get a promise that actually settles, which the
   * flag this used to be could not give them — it was set before the first `await`, so anyone
   * awaiting a flush that had just been fired and forgotten returned immediately.
   */
  /*
   * Sent before read, and the order matters more than it looks. `sendOp` builds its body out
   * of the mirror, so a pull that landed first would have the server's row sitting where the
   * unsent edit was — and the write would then hand that row straight back as a fresh one. The
   * edit would be gone with no refusal, no 409 and nothing said: the very comparison
   * last-write-wins exists to make would never happen. Sending first, a write either wins on
   * its own moment or is refused and yields to the row it lost to.
   *
   * `flush` pulls after a pass that sent something, so the pull below only costs a request
   * where the queue was empty.
   */
  async function syncNow(): Promise<void> {
    await flush()
    await pull()
  }

  function flush(): Promise<void> {
    running ??= sendQueued()
      /*
       * A write is followed by a pull: the server has more to say about what was just sent —
       * the row as it wrote it, and anything else that moved while this device was writing.
       * A pass that sent nothing asks for nothing; there is no news a queue nobody wrote to
       * could have made.
       */
      .then(async (sent) => {
        if (sent) {
          await pull()
        }
      })
      .finally(() => {
        running = null
      })
    return running
  }

  /**
   * Waits for the queue to be sent, not merely for whatever pass is running. A pass already
   * under way may have read the queue before the op being waited on joined it, so a second
   * one follows the first.
   */
  async function drain(): Promise<void> {
    await flush()
    await flush()
  }

  /**
   * The active template as it was last worked out, against the state it was worked out from.
   * Every page that offers a workout asks for it the moment it opens, and the answer costs a
   * round trip; nothing in it moves until the run, its script, or what has been logged under
   * it does, so the key is what says when it has to be worked out again.
   */
  let activeHeld: {
    key: string
    value: Promise<ActiveTemplate | null>
    /** Filled in once the answer lands, so a page can open with it rather than wait again. */
    landed?: { value: ActiveTemplate | null }
  } | null = null

  /**
   * The script a run is following. A run of one of your own templates follows what that
   * template says now — editing it is what moves the run on, and the server does the same to
   * the version underneath. Anybody else's is the version they started, held on the device so
   * a plan still comes out with no connection.
   */
  function scriptOfRun(enrollment: Enrollment): { name: string; script: string } | null {
    const template =
      enrollment.templateId === null
        ? undefined
        : state.templates.find((held) => held.id === enrollment.templateId)
    if (template) {
      return { name: template.name, script: template.script }
    }
    const version = state.versions.find((held) => held.id === enrollment.versionId)
    return version ? { name: version.name, script: version.script } : null
  }

  /** Everything the active template is worked out from, as one string. */
  function activeTemplateKey(): string {
    const enrollment = state.enrollments.find((held) => held.endedAt === null)
    if (!enrollment) {
      return 'none'
    }
    const template = state.templates.find((held) => held.id === enrollment.templateId)
    const under = state.workouts.filter((workout) => workout.enrollmentId === enrollment.id)
    const latest = under.reduce((newest, workout) => Math.max(newest, workout.finishedAt ?? 0), 0)
    return [
      enrollment.id,
      enrollment.startedAt,
      // Moving the anchor moves which workout today is, so the kept answer is not the answer.
      enrollment.anchorDay ?? 'unanchored',
      // A strict schedule's answer changes when the local calendar day does.
      daysIntoCycle(enrollment.startedAt, now(), enrollment.anchorDay ?? null),
      enrollment.versionId,
      template?.updatedAt ?? 'gone',
      // A run of somebody else's template plans once its version has been fetched, not before.
      scriptOfRun(enrollment) === null ? 'unread' : 'read',
      under.length,
      latest,
    ].join('|')
  }

  /** The active template, worked out from scratch. Everything else goes through the key above. */
  async function workOutActiveTemplate(): Promise<ActiveTemplate | null> {
    const enrollment = state.enrollments.find((held) => held.endedAt === null)
    if (!enrollment) {
      return null
    }
    const template = state.templates.find((held) => held.id === enrollment.templateId) ?? null
    const found = scriptOfRun(enrollment)
    if (!found) {
      return null
    }
    const { name, script } = found
    let node
    try {
      node = parseTemplate(script)
    } catch (error) {
      return {
        enrollment,
        template,
        name,
        script,
        node: null,
        history: { sessions: [], allSets: {} },
        next: null,
        error: error instanceof Error ? error.message : String(error),
      }
    }
    const names = [
      ...new Set(
        node.workouts.flatMap((workout) =>
          workout.items.flatMap((item) =>
            item.type === 'exercise'
              ? [item.exercise.name]
              : item.exercises.map((exercise) => exercise.name),
          ),
        ),
      ),
    ]
    const context = await service.templateContext(names, enrollment.id)
    const history = toHistory(context)
    const day = daysIntoCycle(enrollment.startedAt, now(), enrollment.anchorDay ?? null)
    return {
      enrollment,
      template,
      name,
      script,
      node,
      history,
      next: nextWorkout(node, history, day),
      error: null,
    }
  }

  /** Starting a run: whatever was active ends, and the new one is saved and queued. */
  async function startRun(enrollment: Enrollment): Promise<Enrollment> {
    const ended = state.enrollments.map((held) =>
      held.endedAt === null ? { ...held, endedAt: enrollment.startedAt } : held,
    )
    for (const held of ended) {
      await store.saveEnrollment(held)
    }
    await store.saveEnrollment(enrollment)
    setState({ enrollments: [enrollment, ...ended].sort((a, b) => b.startedAt - a.startedAt) })
    await enqueue({ kind: 'enroll', enrollmentId: enrollment.id })
    void flush()
    return enrollment
  }

  /**
   * The exercises row as the list route used to answer it: every exercise with sets, most
   * recently done first, each marked with the latest set of it that has a thumbnail. Worked
   * out here now, the whole of the account's sets being held.
   */
  function recentExercisesOf(sets: readonly LoggedSet[]): RecentExercise[] {
    const byExercise = new Map<string, RecentExercise>()
    // Newest first, so the first set of a name is the one that names and marks the entry.
    for (const set of [...sets].sort(byNewestFirst)) {
      const key = normalizeExerciseName(set.exerciseName)
      const known = byExercise.get(key)
      if (!known) {
        byExercise.set(key, {
          name: set.exerciseName,
          records: set.records,
          lastPerformedAt: set.performedAt,
          thumbnailSetId: set.hasThumbnail ? set.id : null,
        })
      } else if (known.thumbnailSetId === null && set.hasThumbnail) {
        byExercise.set(key, { ...known, thumbnailSetId: set.id })
      }
    }
    return [...byExercise.values()].sort((a, b) => b.lastPerformedAt - a.lastPerformedAt)
  }

  /**
   * Whether this device has ever read the account. Until it has, the mirror's kinds answer
   * null rather than empty: a browser that has never pulled the feed knows nothing about the
   * diary, and an empty list from it would be a claim it has not earned. Once it has read the
   * account once, what it holds is an answer — which is what an offline launch shows.
   */
  let everRead = false

  /**
   * The context a template is evaluated against, worked out the way the route that used to
   * answer for it worked it out: the run's finished workouts oldest first, each carrying the
   * sets it planned in the order they were planned, with the numbers of whichever were logged;
   * and every set of the named exercises, folded by name, oldest first.
   */
  function contextOf(names: readonly string[], enrollmentId: string | null): TemplateContext {
    const sessions: ContextSession[] = []
    if (enrollmentId !== null) {
      const under = state.workouts
        .filter((workout) => workout.enrollmentId === enrollmentId && workout.finishedAt !== null)
        .sort((a, b) => a.startedAt - b.startedAt || (a.id < b.id ? -1 : 1))
      for (const workout of under) {
        sessions.push({
          workoutId: workout.id,
          workout: workout.templateWorkout ?? null,
          startedAt: workout.startedAt,
          // A set the assistant asked for was never the template's, so no progression reads it.
          sets: (workout.plan ?? [])
            .filter((entry) => entry.byAssistant !== true)
            .map((entry): ContextSet => {
              const setId = entry.setId ?? null
              const logged = setId === null ? null : (findSet(setId) ?? null)
              return {
                exercise: entry.exerciseName,
                warmup: entry.isWarmup,
                // A planned set that was passed over is written down as passed over, which is
                // what tells a progression a failed session from a shorter one.
                logged: setId !== null,
                target: entry.target,
                reps: logged?.reps ?? null,
                weightKg: logged?.weightKg ?? null,
                durationSeconds: logged?.durationSeconds ?? null,
                distanceMeters: logged?.distanceMeters ?? null,
                rpe: logged?.rpe ?? null,
              }
            }),
        })
      }
    }

    /*
     * An exercise is matched by its name, folded — the template names exercises in text, and
     * so does every set. The catalog is not consulted: a name is known here because a set
     * carries it, which is the only sense in which the name exists at all.
     */
    const history: Record<string, ContextSet[]> = {}
    if (names.length === 0) {
      return { sessions, history }
    }
    const keys = new Set(names.map(normalizeExerciseName))
    const oldestFirst = [...state.sets].sort(
      (a, b) => a.performedAt - b.performedAt || (a.id < b.id ? -1 : 1),
    )
    for (const set of oldestFirst) {
      const key = normalizeExerciseName(set.exerciseName)
      if (!keys.has(key)) {
        continue
      }
      const bucket = history[key] ?? []
      bucket.push({
        exercise: set.exerciseName,
        reps: set.reps ?? null,
        weightKg: set.weightKg ?? null,
        durationSeconds: set.durationSeconds ?? null,
        distanceMeters: set.distanceMeters ?? null,
        rpe: set.rpe ?? null,
        warmup: set.isWarmup,
        logged: true,
        target: null,
      })
      history[key] = bucket
    }
    return { sessions, history }
  }

  /**
   * How many of each of a template's workouts the account has done, across every run of it.
   * A run is of the template when the version it follows belongs to it — which is how a run
   * of somebody else's copy still counts towards the original — or, for a run started here
   * and not yet sent, when it names the template outright.
   */
  function workoutsDoneOf(templateId: string): WorkoutsDone[] {
    const versions = new Set(
      state.versions.filter((version) => version.templateId === templateId).map((v) => v.id),
    )
    const runs = new Set(
      state.enrollments
        .filter(
          (enrollment) =>
            versions.has(enrollment.versionId) || enrollment.templateId === templateId,
        )
        .map((enrollment) => enrollment.id),
    )
    const counts = new Map<string, number>()
    for (const workout of state.workouts) {
      const under = workout.enrollmentId ?? null
      const which = workout.templateWorkout ?? null
      if (workout.finishedAt !== null && under !== null && runs.has(under) && which !== null) {
        counts.set(which, (counts.get(which) ?? 0) + 1)
      }
    }
    return [...counts]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || (a.name < b.name ? -1 : 1))
  }

  /** One change laid into the mirror: the row it carries, or the row it says is gone. */
  async function apply(change: Change): Promise<void> {
    if (change.deleted || change.row === undefined) {
      await store.removeRow(change.kind, change.id)
      return
    }
    /*
     * A workout arrives with the sets it planned under the name the wire gives them, and is
     * held under the name the rest of this app uses. Nothing else is renamed on the way in.
     */
    if (change.kind === 'workout') {
      await store.putRow('workout', change.id, workoutFromWire(change.row as WorkoutRow))
      return
    }
    await store.putRow(change.kind, change.id, change.row as never)
  }

  /** Everything the mirror holds, read back into memory for the screens. */
  async function rebuild(): Promise<void> {
    const [sets, workouts, templates, enrollments, versions, mirror] = await Promise.all([
      store.loadSets(),
      store.loadWorkouts(),
      store.loadTemplates(),
      store.loadEnrollments(),
      store.loadVersions(),
      readMirror(),
    ])
    const current = workouts.find((workout) => workout.finishedAt === null) ?? null
    setState({
      sets: [...sets].sort(byNewestFirst),
      workouts: workouts.filter((w) => w.finishedAt !== null).sort(byRecentlyFinished),
      currentWorkout: current,
      templates: [...templates].sort(byNewestUpdate),
      enrollments: [...enrollments].sort((a, b) => b.startedAt - a.startedAt),
      versions,
      recentExercises: recentExercisesOf(sets),
      // Everything the account has is held, so there is no older page to reach for.
      timelineComplete: true,
      timelineIndex: indexOfSets(sets),
      ...mirror,
    })
  }

  /** The kinds no typed reader of the store owns, as the screens read them. */
  async function readMirror(): Promise<Partial<DataState>> {
    if (!everRead) {
      return {
        weighins: null,
        metrics: null,
        entries: null,
        days: null,
        goals: null,
        foods: null,
        favourites: null,
        meals: null,
        mealFavourites: null,
        mealLogs: null,
        settings: null,
      }
    }
    const [
      weighins,
      metrics,
      entries,
      days,
      goals,
      foods,
      favourites,
      meals,
      mealFavourites,
      mealLogs,
      settings,
    ] = await Promise.all([
      store.loadRows('weighin'),
      store.loadRows('metric'),
      store.loadRows('entry'),
      store.loadRows('day'),
      store.loadRows('goal'),
      store.loadRows('food'),
      store.loadRows('favourite'),
      store.loadRows('meal'),
      store.loadRows('mealFavourite'),
      store.loadRows('mealLog'),
      store.loadRows('settings'),
    ])
    return {
      weighins: [...weighins.values()],
      metrics: [...metrics.values()],
      entries: [...entries.values()],
      days: [...days.values()],
      goals: [...goals.values()],
      foods: [...foods.values()],
      favourites: [...favourites.values()],
      meals: [...meals.values()].map(mealAsKept),
      mealFavourites: [...mealFavourites.values()].map((row) => ({
        ...row,
        meal: mealAsKept(row.meal),
      })),
      mealLogs: [...mealLogs.values()].map(mealAsKept),
      settings: settings.get(SETTINGS_ID) ?? null,
    }
  }

  /**
   * The feed, from wherever this device last got to, looping while the server says there is
   * more. Quietly a no-op with no connection: the mirror stays what it was, which is the whole
   * of what the screens read either way.
   */
  let pulling: Promise<void> | null = null
  /** The first pull of the session, which is what "everything has arrived" means here. */
  let firstPull: Promise<void> | null = null

  /**
   * Waits for this browser to have read the account, and says whether it has. A pull finishing
   * is not the same as the account being read — a pull that failed finishes too — and the
   * difference is between a page saying "you have logged none of this" and saying it falsely
   * to somebody with years of it.
   */
  async function read(): Promise<boolean> {
    await (firstPull ?? pull())
    return everRead
  }
  function pull(): Promise<void> {
    pulling ??= pullOnce().finally(() => {
      pulling = null
    })
    firstPull ??= pulling
    return pulling
  }

  async function pullOnce(): Promise<void> {
    // The catalog is read only and public, and is no part of the account's own feed.
    try {
      const exercises = await api.listExercises()
      await store.replaceExercises(exercises)
      setState({ exercises })
    } catch {
      // Offline — whatever the device holds of the catalog still stands.
    }
    try {
      let since = await store.loadCursor()
      for (;;) {
        const page = await api.sync({ since })
        for (const change of page.changes) {
          await apply(change)
        }
        since = page.next
        await store.saveCursor(since)
        if (!page.more) {
          break
        }
      }
      /*
       * Read through to the end. From here what this browser holds is the account, and a later
       * read that fails leaves it standing rather than emptying it.
       */
      await store.saveAccountRead()
      everRead = true
    } catch {
      /*
       * Offline, or the server said no. Where this browser has read the account before, what
       * the mirror holds is the account and it stands.
       *
       * Where it has not, the mirror holds nothing — and settling here would have every screen
       * answer "you have none of that" to somebody with years of it. The account is not empty;
       * it is unread. So `everRead` stays false and the gates go on waiting.
       */
    }
    await rebuild()
    /*
     * `couldNotBeRead` is what lets a screen say why it is empty-handed instead of spinning at
     * somebody forever. Unread is not empty, but it is not "one moment" either.
     */
    setState({ timelineSettled: everRead, synced: everRead, couldNotBeRead: !everRead })
  }

  const service: DataService = {
    getState: () => state,
    subscribe: (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },

    async init() {
      // What the device already holds, before anything is asked of the server: the mirror is
      // the whole of what the screens read, so a launch with no connection has its account.
      setState({ exercises: await store.loadExercises() })
      // A cursor past nothing is this device having read the account before.
      everRead = await store.loadAccountRead()
      await rebuild()
    },

    refresh: syncNow,

    /*
     * Nothing to fill in: the feed hands over every set the account has, so a day the
     * timeline is about to show is already held. Kept as the shape the timeline asks with.
     */
    loadDays: () => Promise.resolve(),

    /**
     * The same, and for the same reason: the whole history of every exercise is held once the
     * feed has been pulled. So there is nothing to fetch — only the first pull to wait for,
     * which is what a page drawing a curve over the whole history is waiting for.
     */
    async loadExerciseHistory() {
      return await read()
    },

    async loadSet(id) {
      // Deleted here and not yet sent: the server would still hand it back, and the page it
      // was deleted from must not be told it exists.
      if (deletedHere.has(id)) {
        return 'gone'
      }
      if (findSet(id) !== null) {
        return 'found'
      }
      let set: LoggedSet
      try {
        set = await api.getSet(id)
      } catch (error) {
        return lookupFailure(error)
      }
      if (deletedHere.has(set.id) || findSet(set.id) !== null) {
        return deletedHere.has(set.id) ? 'gone' : 'found'
      }
      await store.saveSet(set)
      setState({ sets: [...state.sets, set].sort(byNewestFirst) })
      return 'found'
    },

    async loadWorkout(id) {
      const held =
        state.workouts.some((workout) => workout.id === id) || state.currentWorkout?.id === id
      if (held) {
        return 'found'
      }
      let answer: { workout: WorkoutRow; sets: LoggedSet[] }
      try {
        answer = await api.getWorkout(id)
      } catch (error) {
        return lookupFailure(error)
      }
      // Read off the wire the same way a feed row is, so a workout reached by its address
      // carries the plan it played exactly as one that arrived with the feed does.
      const workout = workoutFromWire(answer.workout)
      const known = new Set(state.sets.map((set) => set.id))
      const arrived = answer.sets.filter((set) => !known.has(set.id) && !deletedHere.has(set.id))
      for (const set of arrived) {
        await store.saveSet(set)
      }
      await store.saveWorkout(workout)
      setState({
        workouts: state.workouts.some((held) => held.id === workout.id)
          ? state.workouts
          : [...state.workouts, workout].sort(byRecentlyFinished),
        ...(arrived.length > 0 ? { sets: [...state.sets, ...arrived].sort(byNewestFirst) } : {}),
      })
      return 'found'
    },

    /** There is no older page: the feed brought the whole of the account's sets. */
    loadOlderSets: () => Promise.resolve(),

    flush,
    drain,

    async editSet(id, input) {
      const existing = findSet(id)
      if (!existing) {
        throw new Error(`No set with id ${id}`)
      }
      const numbers = numbersOf(input)
      const edited: LoggedSet = {
        ...existing,
        exerciseName: input.exerciseName,
        records: recordsOf(numbers),
        performedAt: input.performedAt,
        ...numbers,
        isWarmup: input.isWarmup ?? existing.isWarmup,
      }
      await store.saveSet(edited)
      const ops = await store.loadOps()
      if (!ops.some((op) => op.kind === 'put-set' && op.setId === id)) {
        await enqueue({ kind: 'put-set', setId: id })
      }
      setState({ sets: state.sets.map((s) => (s.id === id ? edited : s)).sort(byNewestFirst) })
      void flush()
    },

    async deleteSet(id) {
      const ops = await store.loadOps()
      const neverSynced = ops.some((op) => op.kind === 'put-set' && op.setId === id)
      for (const op of ops) {
        if ('setId' in op && op.setId === id) {
          await store.removeOp(op.id)
          droppedMidPass.add(op.id)
        }
      }
      await store.removeSet(id)
      setState({ sets: state.sets.filter((set) => set.id !== id) })
      if (!neverSynced) {
        deletedHere.add(id)
        await enqueue({ kind: 'delete-set', setId: id })
        void flush()
      }
    },

    async saveTemplate(id, script, forkedFromVersionId) {
      const existing = id === null ? undefined : state.templates.find((held) => held.id === id)
      const template: WorkoutTemplate = {
        id: existing?.id ?? id ?? uuid(),
        name: scriptName(script),
        script,
        forkedFromVersionId: forkedFromVersionId ?? existing?.forkedFromVersionId ?? null,
        publishedVersionId: existing?.publishedVersionId ?? null,
        // Saving is editing, and an edit is not published until it is published.
        publishedIsCurrent: false,
        createdAt: existing?.createdAt ?? now(),
        updatedAt: updatedAtOf(now()),
      }
      await store.saveTemplate(template)
      // The queue is read by a flush that can start at any moment, so nothing is queued
      // before what it points at is in hand.
      setState({
        templates: [template, ...state.templates.filter((held) => held.id !== template.id)].sort(
          byNewestUpdate,
        ),
      })
      await enqueue({ kind: 'put-template', templateId: template.id })
      void flush()
      return template
    },

    async deleteTemplate(id) {
      await store.removeTemplate(id)
      /*
       * The runs of it stay. They keep the name they ran under and stop pointing at a template,
       * which is what the server does to them too — the history is not the template's to take
       * away. What they were following is kept as a version so the active one can still plan.
       */
      const template = state.templates.find((held) => held.id === id)
      /*
       * Only the run still going, and only when nothing is held for it already: a run that
       * ended followed some earlier version, and writing today's script under its id would
       * have it replan from the wrong one if it were ever restored.
       */
      const active = state.enrollments.find(
        (held) => held.templateId === id && held.endedAt === null,
      )
      if (
        template &&
        active !== undefined &&
        active.versionId.length > 0 &&
        !state.versions.some((held) => held.id === active.versionId)
      ) {
        await store.saveVersion({
          id: active.versionId,
          templateId: null,
          version: 0,
          name: active.templateName,
          script: template.script,
          createdAt: active.startedAt,
        })
      }
      const cut = state.enrollments.map((held) =>
        held.templateId === id ? { ...held, templateId: null, listedVersionId: null } : held,
      )
      for (const held of cut) {
        await store.saveEnrollment(held)
      }
      const kept = await store.loadVersions()
      setState({
        templates: state.templates.filter((held) => held.id !== id),
        enrollments: cut,
        versions: kept,
      })
      await enqueue({ kind: 'delete-template', templateId: id })
      void flush()
    },

    async publishTemplate(id) {
      /*
       * What goes into Explore is the script as it stands, so a write of this template still
       * waiting has to land first. Only then: the queue is FIFO, so draining it for a template
       * with nothing outstanding would make Publish wait behind somebody's video upload for
       * no reason. Publishing itself never joins the queue — it acts on something shared, and
       * needs a connection either way.
       */
      const queued = await store.loadOps()
      if (queued.some((op) => op.kind === 'put-template' && op.templateId === id)) {
        await drain()
      }
      const listing = await api.publishTemplate(id)
      const template = state.templates.find((held) => held.id === id)
      if (template) {
        const published: WorkoutTemplate = {
          ...template,
          publishedVersionId: listing.versionId,
          publishedIsCurrent: true,
        }
        await store.saveTemplate(published)
        setState({
          templates: state.templates.map((held) => (held.id === id ? published : held)),
        })
      }
      return listing
    },

    async unpublishTemplate(id) {
      await api.unpublishTemplate(id)
      const template = state.templates.find((held) => held.id === id)
      if (template) {
        const withdrawn: WorkoutTemplate = {
          ...template,
          publishedVersionId: null,
          publishedIsCurrent: false,
        }
        await store.saveTemplate(withdrawn)
        setState({
          templates: state.templates.map((held) => (held.id === id ? withdrawn : held)),
        })
      }
    },

    explore: (query) => api.explore(query),

    exploreTemplate: (templateId) => api.exploreTemplate(templateId),
    /*
     * What you have done of a template stays the server's to count, alone among the lists
     * this file now works out for itself. The page says nothing until the counting answers,
     * and says nothing of a workout it could not count — behavior every app shares and no
     * spec of ours describes as a local sum. Counting it here would quietly make both of
     * those unsayable; see the sync contract, where it is the one list route still standing.
     */
    /**
     * What you have done of a template, by the workout that was performed, across every run
     * of it — counted from the account's own workouts, the mirror holding all of them.
     */
    async templateWorkoutsDone(templateId) {
      await read()
      return workoutsDoneOf(templateId)
    },

    async copyTemplate(listing) {
      // A copy is yours from here: a template of your own, remembering only where it came from.
      return await service.saveTemplate(null, listing.script, listing.versionId)
    },

    async enroll(templateId, anchorDay) {
      const template = state.templates.find((held) => held.id === templateId)
      if (!template) {
        throw new Error(`No template with id ${templateId}`)
      }
      const startedAt = now()
      const enrollment: Enrollment = {
        id: uuid(),
        // The server mints the version this follows; until this lands there is none to name.
        versionId: '',
        templateId: template.id,
        templateName: template.name,
        authorName: null,
        mine: true,
        listedVersionId: template.publishedVersionId ?? null,
        startedAt,
        endedAt: null,
        anchorDay,
      }
      return await startRun(enrollment)
    },

    async enrollInListing(listing, anchorDay) {
      const startedAt = now()
      // The script comes with the listing, so the run can be planned before anything is sent.
      await store.saveVersion({
        id: listing.versionId,
        templateId: listing.templateId,
        version: 0,
        name: listing.name,
        script: listing.script,
        createdAt: listing.publishedAt,
      })
      setState({ versions: await store.loadVersions() })
      return await startRun({
        id: uuid(),
        versionId: listing.versionId,
        templateId: listing.mine ? listing.templateId : null,
        templateName: listing.name,
        authorName: listing.authorName,
        mine: listing.mine,
        listedVersionId: listing.versionId,
        startedAt,
        endedAt: null,
        anchorDay,
      })
    },

    async setRunAnchor(enrollmentId, anchorDay) {
      const moved = state.enrollments.map((held) =>
        held.id === enrollmentId ? { ...held, anchorDay } : held,
      )
      const held = moved.find((entry) => entry.id === enrollmentId)
      if (!held) {
        return
      }
      await store.saveEnrollment(held)
      setState({ enrollments: moved })
      await enqueue({ kind: 'set-anchor', enrollmentId })
      void flush()
    },

    async moveRunToVersion(enrollmentId, versionId) {
      // Online only: the version being moved to is one this device has never seen.
      const moved = await api.moveEnrollmentToVersion(enrollmentId, versionId)
      const version = await api.getTemplateVersion(versionId)
      await store.saveVersion(version)
      await store.saveEnrollment(moved)
      setState({
        enrollments: state.enrollments.map((held) => (held.id === moved.id ? moved : held)),
        versions: await store.loadVersions(),
      })
    },

    async endEnrollment(id) {
      const endedAt = now()
      const enrollments = state.enrollments.map((held) =>
        held.id === id ? { ...held, endedAt } : held,
      )
      const ended = enrollments.find((held) => held.id === id)
      if (ended) {
        await store.saveEnrollment(ended)
      }
      setState({ enrollments })
      await enqueue({ kind: 'end-enrollment', enrollmentId: id })
      void flush()
    },

    async resumeEnrollment(id) {
      const endedAt = now()
      const enrollments = state.enrollments.map((held) => {
        if (held.id === id) {
          return { ...held, endedAt: null }
        }
        // Only one runs at a time, so whatever else was active ends here.
        return held.endedAt === null ? { ...held, endedAt } : held
      })
      for (const held of enrollments) {
        await store.saveEnrollment(held)
      }
      setState({ enrollments })
      await enqueue({ kind: 'enroll', enrollmentId: id })
      void flush()
    },

    async restoreEnrollment(id) {
      const current = state.enrollments.find((held) => held.endedAt === null && held.id !== id)
      const enrollments = state.enrollments
        .filter((held) => held.id !== current?.id)
        .map((held) => (held.id === id ? { ...held, endedAt: null } : held))
      if (current) {
        await store.removeEnrollment(current.id)
      }
      const restored = enrollments.find((held) => held.id === id)
      if (restored) {
        await store.saveEnrollment(restored)
      }
      setState({ enrollments })
      if (current) {
        await enqueue({ kind: 'delete-enrollment', enrollmentId: current.id })
      }
      await enqueue({ kind: 'enroll', enrollmentId: id })
      void flush()
    },

    async activeTemplate() {
      const key = activeTemplateKey()
      if (activeHeld?.key !== key) {
        const value = workOutActiveTemplate()
        // Typed as what it is about to become, so filling `landed` in later is allowed.
        const entry: NonNullable<typeof activeHeld> = { key, value }
        activeHeld = entry
        void value.then(
          (landed) => {
            entry.landed = { value: landed }
          },
          // A failed answer is not worth keeping — the next asking should try again.
          () => {
            if (activeHeld === entry) {
              activeHeld = null
            }
          },
        )
      }
      return activeHeld.value
    },

    heldActiveTemplate() {
      return activeHeld?.key === activeTemplateKey() ? (activeHeld.landed ?? null) : null
    },

    millisecondsUntilNextCalendarDay() {
      const at = now()
      const next = new Date(at)
      next.setHours(24, 0, 0, 0)
      return Math.max(1, next.getTime() - at)
    },

    /**
     * What a template needs before it can be evaluated: the sessions of a run, and every set
     * ever logged of the exercises the script names. Evaluation is pure, so what it is given
     * has to be exact rather than whatever happens to be to hand — which is why this used to
     * be the server's to answer. The mirror holds the whole of both now, so it is worked out
     * here, from the same rows and in the same order the route worked it out in.
     */
    async templateContext(names: string[], enrollmentId: string | null): Promise<TemplateContext> {
      // Not before the account has been read: a plan drawn from half a history is a plan
      // that changes under somebody once the rest of it lands.
      await read()
      return contextOf(names, enrollmentId)
    },

    async renameWorkout(id, name) {
      const workout = state.workouts.find((w) => w.id === id)
      if (!workout) {
        throw new Error(`No workout with id ${id}`)
      }
      const renamed: Workout = { ...workout, name }
      await store.saveWorkout(renamed)
      await enqueue({ kind: 'put-workout', workoutId: id })
      setState({ workouts: state.workouts.map((w) => (w.id === id ? renamed : w)) })
      void flush()
    },

    async deleteWorkout(id) {
      const ops = await store.loadOps()
      const neverSynced = ops.some((op) => op.kind === 'put-workout' && op.workoutId === id)
      for (const op of ops) {
        if ('workoutId' in op && op.workoutId === id) {
          await store.removeOp(op.id)
        }
      }
      await store.removeWorkout(id)
      // A workout goes with everything in it. The server cascades the same way, so the sets
      // need no delete of their own queued behind the workout's.
      const held = state.sets.filter((set) => set.workoutId === id)
      const touched = new Set(held.map((set) => dayKey(set.performedAt)))
      for (const set of held) {
        for (const op of ops) {
          if ('setId' in op && op.setId === set.id) {
            await store.removeOp(op.id)
          }
        }
        await store.removeSet(set.id)
      }
      const left = state.sets.filter((set) => set.workoutId !== id)
      setState({
        workouts: state.workouts.filter((w) => w.id !== id),
        currentWorkout: state.currentWorkout?.id === id ? null : state.currentWorkout,
        sets: left,
        timelineIndex: reindexed(state.timelineIndex, touched, left),
      })
      if (!neverSynced) {
        await enqueue({ kind: 'delete-workout', workoutId: id })
        void flush()
      }
    },

    workoutWithSets(id) {
      const workout =
        state.workouts.find((w) => w.id === id) ??
        (state.currentWorkout?.id === id ? state.currentWorkout : null)
      if (!workout) {
        return null
      }
      const sets = state.sets
        .filter((set) => set.workoutId === id)
        .sort((a, b) => a.performedAt - b.performedAt)
      return { workout, sets }
    },

    mediaUrl: (setId, slot) => api.mediaUrl(setId, slot),

    async keep(kind, id, row) {
      await store.putRow(kind, id, row)
      await rebuild()
    },
    async drop(kind, id) {
      await store.removeRow(kind, id)
      await rebuild()
    },
    async logEntries(entries) {
      for (const entry of entries) {
        await store.putRow('entry', entry.id, entry)
        await enqueue({ kind: 'put-entry', entryId: entry.id })
      }
      await rebuild()
      void drain()
    },
    async keepAll(kind, rows, gone = []) {
      for (const { id, row } of rows) {
        await store.putRow(kind, id, row)
      }
      for (const id of gone) {
        await store.removeRow(kind, id)
      }
      await rebuild()
    },

    /**
     * The mirror belongs to the account that filled it, so signing out drops the whole of it —
     * cursor included, which is what makes the next account read the feed from its beginning
     * rather than from where the last one got to.
     */
    async reset() {
      await store.clear()
      activeHeld = null
      everRead = false
      // The promise belongs to the account that was signed in: left standing, the next account's
      // first question would be answered off it, instantly, against a mirror just emptied.
      firstPull = null
      droppedMidPass.clear()
      deletedHere.clear()
      setState({
        timelineSettled: false,
        synced: false,
        couldNotBeRead: false,
        exercises: [],
        sets: [],
        workouts: [],
        currentWorkout: null,
        recentExercises: [],
        timelineComplete: false,
        timelineIndex: [],
        templates: [],
        enrollments: [],
        versions: [],
        weighins: null,
        metrics: null,
        entries: null,
        days: null,
        goals: null,
        foods: null,
        favourites: null,
        meals: null,
        mealFavourites: null,
        mealLogs: null,
        settings: null,
      })
    },
  }

  return service
}
